import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { applyDocumentLang } from "./documentLang";
import { en, type MessageKey } from "./locales/en";
import { tr } from "./locales/tr";

export type Locale = "en" | "tr";

const STORAGE_KEY = "semanticad.locale";

const catalogs: Record<Locale, Record<MessageKey, string>> = { en, tr };

export function resolveLocale(raw: string | null | undefined): Locale {
  if (raw === "tr" || raw === "en") return raw;
  return "en";
}

export function getStoredLocale(): Locale {
  try {
    return resolveLocale(localStorage.getItem(STORAGE_KEY));
  } catch {
    return "en";
  }
}

export function setStoredLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* ignore quota / private mode */
  }
}

export type TranslateFn = (
  key: MessageKey,
  vars?: Record<string, string | number>,
) => string;

export function translate(
  locale: Locale,
  key: MessageKey,
  vars?: Record<string, string | number>,
): string {
  const catalog = catalogs[locale] ?? en;
  let text = catalog[key] ?? en[key] ?? String(key);
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      text = text.split(`{${k}}`).join(String(v));
    }
  }
  return text;
}

interface LanguageContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: TranslateFn;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({
  children,
  initialLocale,
}: {
  children: ReactNode;
  /** Override for tests; defaults to stored / English. */
  initialLocale?: Locale;
}) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const loc = initialLocale ?? getStoredLocale();
    applyDocumentLang(loc);
    return loc;
  });

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    setStoredLocale(next);
    applyDocumentLang(next);
  }, []);

  // Keep <html lang> in sync (layout effect = before paint of children).
  useLayoutEffect(() => {
    applyDocumentLang(locale);
  }, [locale]);

  const t = useCallback<TranslateFn>(
    (key, vars) => translate(locale, key, vars),
    [locale],
  );

  const value = useMemo(
    () => ({ locale, setLocale, t }),
    [locale, setLocale, t],
  );

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
}

const fallbackT: TranslateFn = (key, vars) => translate("en", key, vars);

/** Outside a provider, defaults to English (safe for tests / partial trees). */
export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    return {
      locale: "en",
      setLocale: () => {
        /* no-op without provider */
      },
      t: fallbackT,
    };
  }
  return ctx;
}

/** Shortcut: only the translator. */
export function useT(): TranslateFn {
  return useLanguage().t;
}
